import { useEffect, useRef, useState, type ReactNode } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/lib/cn'
import { usePrefersReducedMotion } from '@/lib/motion'

type Props = {
  children: ReactNode
  className?: string
  /** Stagger when multiple siblings scroll into view together. */
  index?: number
}

/**
 * Home-only: fade + rise when the block enters the viewport while scrolling.
 */
export function HomeScrollReveal({ children, className, index = 0 }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const reduceMotion = usePrefersReducedMotion()
  // Visible up front when motion is off, or when there's no IntersectionObserver
  // to drive the reveal (older runtimes / jsdom) — avoids a setState in the effect.
  const [visible, setVisible] = useState(
    () => reduceMotion || typeof IntersectionObserver === 'undefined',
  )

  useEffect(() => {
    if (reduceMotion || typeof IntersectionObserver === 'undefined') return
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true)
          io.disconnect()
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -6% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [reduceMotion])

  return (
    <motion.div
      ref={ref}
      className={cn(className)}
      initial={reduceMotion ? false : { opacity: 0, y: 20 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{
        duration: 0.55,
        delay: index * 0.14,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      {children}
    </motion.div>
  )
}
